import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/layouts/AppLayout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SubmitChart from "@/pages/coder/SubmitChart";
import History from "@/pages/coder/History";
import SearchCharts from "@/pages/coder/SearchCharts";
import Productivity from "@/pages/coder/Productivity";
import MyQuality from "@/pages/coder/MyQuality";
import OasisItems from "@/pages/coder/OasisItems";
import FullOasis from "@/pages/coder/FullOasis";
import SavedOasis from "@/pages/coder/SavedOasis";
import AssignedCharts from "@/pages/qa/AssignedCharts";
import AuditForm from "@/pages/qa/AuditForm";
import AllCharts from "@/pages/admin/AllCharts";
import AssignCharts from "@/pages/admin/AssignCharts";
import Users from "@/pages/admin/Users";
import UnassignedPool from "@/pages/qa/UnassignedPool";
import Acknowledgements from "@/pages/coder/Acknowledgements";
import Reports from "@/pages/Reports";
import ChartDetail from "@/pages/ChartDetail";
import ChangePassword from "@/pages/ChangePassword";

function ProtectedRoute({ roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function PrivateRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Forced-password-change route — accessible to logged-in users only, no layout */}
            <Route element={<PrivateRoute />}>
              <Route path="/change-password" element={<ChangePassword />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/charts/:chartId" element={<ChartDetail />} />

                {/* Coder */}
                <Route element={<ProtectedRoute roles={["coder", "admin"]} />}>
                  <Route path="/coder/submit" element={<SubmitChart />} />
                  <Route path="/coder/history" element={<History />} />
                  <Route path="/coder/search" element={<SearchCharts />} />
                  <Route path="/coder/productivity" element={<Productivity />} />
                  <Route path="/coder/quality" element={<MyQuality />} />
                  <Route path="/coder/acknowledgements" element={<Acknowledgements />} />
                  <Route path="/oasis/items" element={<OasisItems />} />
                  <Route path="/oasis/full" element={<FullOasis />} />
                </Route>

                {/* Reports — QA / Team Lead / Manager / Admin */}
                <Route element={<ProtectedRoute roles={["qa", "team_lead", "manager", "admin"]} />}>
                  <Route path="/reports" element={<Reports />} />
                </Route>

                {/* QA */}
                <Route element={<ProtectedRoute roles={["qa", "admin", "manager", "team_lead"]} />}>
                  <Route path="/qa/assigned" element={<AssignedCharts />} />
                  <Route path="/qa/pool" element={<UnassignedPool />} />
                  <Route path="/qa/audit/:chartId" element={<AuditForm />} />
                </Route>

                {/* Admin / Manager / Team Lead */}
                <Route element={<ProtectedRoute roles={["admin", "manager", "team_lead"]} />}>
                  <Route path="/admin/charts" element={<AllCharts />} />
                  <Route path="/admin/assign" element={<AssignCharts />} />
                  <Route path="/admin/users" element={<Users />} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
